import { BaseLayouter } from './BaseLayouter';
import { Vector3 } from '../../types/Vector3';
import { Quaternion } from '../../types/Quaternion';
import { LayoutData, SpacingMode } from '../LayoutData';

/**
 * ScatterLayouter -- positions POIs pseudo-randomly in a 3D volume.
 * Uses a deterministic seed so the same presentation always produces the same layout.
 */
export class ScatterLayouter extends BaseLayouter {
    constructor() {
        super(
            'scatter',
            'Positions objects randomly in a configurable 3D volume with depth variation',
            {
                scatterWidth: 10,
                scatterHeight: 6,
                scatterDepth: 8,
                scatterSeed: 42
            },
            '2d',
            [SpacingMode.Fixed]
        );
    }

    /** Simple seeded PRNG (mulberry32) for deterministic randomness */
    private seededRandom(seed: number): () => number {
        return () => {
            seed |= 0;
            seed = seed + 0x6D2B79F5 | 0;
            let t = Math.imul(seed ^ seed >>> 15, 1 | seed);
            t = t + Math.imul(t ^ t >>> 7, 61 | t) ^ t;
            return ((t ^ t >>> 14) >>> 0) / 4294967296;
        };
    }

    calculatePosition(index: number, totalCount: number, layoutData: LayoutData): Vector3 {
        const width = this.getParameter(layoutData, 'scatterWidth', 10);
        const height = this.getParameter(layoutData, 'scatterHeight', 6);
        const depth = this.getParameter(layoutData, 'scatterDepth', 8);
        const seed = this.getParameter(layoutData, 'scatterSeed', 42);

        const rng = this.seededRandom(seed + index * 7919); // unique per POI
        // Center the volume around origin
        const x = (rng() - 0.5) * width;
        const y = (rng() - 0.5) * height;
        const z = (rng() - 0.5) * depth;

        return new Vector3(x, y, z);
    }

    calculateOrientation(index: number, totalCount: number, layoutData: LayoutData): Vector3 {
        // All objects face camera (no rotation)
        return new Vector3(0, 0, 0);
    }

    protected calculateOrientationQuaternion(
        position: Vector3,
        index: number,
        totalCount: number,
        layoutData: LayoutData
    ): Quaternion {
        // Identity orientation -- all objects face default direction
        return Quaternion.identity();
    }

    protected getFixedSpacingParameter(layoutData: LayoutData): number {
        // Not directly applicable; return nominal value
        return 1;
    }

    protected calculateUniformSpacing(layoutData: LayoutData, objectCount: number): number {
        // Not applicable for scatter; fall back to fixed
        return this.getFixedSpacingParameter(layoutData);
    }

    clone(): ScatterLayouter {
        return new ScatterLayouter();
    }

    static create(): ScatterLayouter {
        return new ScatterLayouter();
    }
}
